"use client";

import { useEffect } from "react";

import { reportClientError } from "~/lib/observability-client";

/**
 * P0-16 · Root error boundary. Catches errors thrown by the root layout itself,
 * so it must render its own <html>/<body> and can't rely on the app's stylesheet
 * or store theme being present — hence inline styles. Reports to the server intake.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportClientError({
      message: error.message,
      stack: error.stack,
      name: error.name,
      digest: error.digest,
      route: typeof window !== "undefined" ? window.location.pathname : undefined,
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.5rem",
          fontFamily: "system-ui, sans-serif",
          background: "#0b0b0f",
          color: "#f5f5f7",
          textAlign: "center",
          padding: "1rem",
        }}
      >
        <div style={{ maxWidth: "28rem" }}>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, margin: "0 0 0.5rem" }}>
            Something went wrong
          </h1>
          <p style={{ opacity: 0.75, margin: 0 }}>
            An unexpected error occurred. It has been logged and we&apos;re
            looking into it.
          </p>
        </div>
        <button
          type="button"
          onClick={reset}
          style={{
            appearance: "none",
            border: "none",
            borderRadius: "0.5rem",
            padding: "0.625rem 1.25rem",
            fontSize: "0.875rem",
            fontWeight: 600,
            cursor: "pointer",
            background: "#f5f5f7",
            color: "#0b0b0f",
          }}
        >
          Try again
        </button>
      </body>
    </html>
  );
}
